import React, { FC } from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  author?: string;
  datePublished?: string;
  type?: 'website' | 'article';
  url?: string;
  image?: string;
}

const SEO: FC<SEOProps> = ({
  title,
  description,
  keywords,
  author = 'Sebastien Toscano',
  datePublished,
  type = 'website',
  url,
  image = 'https://sebtosca.netlify.app/og-image.jpg', // Default OG image
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sebtosca.netlify.app/';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const canonicalUrl = fullUrl.endsWith('/') ? fullUrl.slice(0, -1) : fullUrl;

  // Enhanced JSON-LD for better Google Rich Results detection
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'BlogPosting' : 'WebSite',
    '@id': canonicalUrl,
    'url': canonicalUrl,
    'name': title,
    'description': description,
    'publisher': {
      '@type': 'Organization',
      'name': 'Sebastien Toscano'
    },
    ...(type === 'article'
      ? {
          'headline': title,
          'datePublished': datePublished,
          'dateModified': datePublished,
          'author': {
            '@type': 'Person',
            'name': author,
            'url': `${siteUrl}/about`
          },
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': canonicalUrl
          }
        }
      : {
          'potentialAction': {
            '@type': 'SearchAction',
            'target': `${siteUrl}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string'
          }
        })
  };

  return (
    <Helmet prioritizeSeoTags>
      {/* Search Engine Verification Tags - Must be first */}
      <meta name="google-site-verification" content="OKHkoJPxFrposOJVCLpti-uEdirEUmStsug_npUbm4s" />
      <meta name="msvalidate.01" content="29CF16879B248F9D5171A19803ED3959" />

      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Sebastien Toscano" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@sebtosca" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article-specific meta tags */}
      {type === 'article' && (
        <>
          <meta property="article:published_time" content={datePublished} />
          <meta property="article:author" content={author} />
        </>
      )}

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd, null, 2) }}
      />
    </Helmet>
  );
};

export default SEO; 